import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './ProductsAdmin.css';
import API_BASE_URL from '../../api';

const TABS = ['active', 'inactive', 'sold', 'deleted'];

// ===== InlineEditor defined OUTSIDE so React doesn't recreate it on every render =====
const InlineEditor = ({ product, field, isEditing, type = 'text', options, inlineEdit, setInlineEdit, onSave, onCancel, onStart, buildCategoryPath }) => (
    <td className="pm-clickable-cell" onClick={(e) => { e.stopPropagation(); onStart(product, field); }}>
    <span>
      {field === 'price'
          ? (product.price != null && product.price !== '' ? `£${product.price}` : '—')
          : field === 'category'
              ? buildCategoryPath(product.category_id) || '—'
              : product[field] || '—'}
    </span>
      <span className="pm-edit-icon" onClick={(e) => { e.stopPropagation(); onStart(product, field); }}>✏️</span>
      {isEditing && (
          <div className="pm-inline-editor" onClick={(e) => e.stopPropagation()}>
            {options ? (
                <select value={inlineEdit.value || ''} onChange={(e) => setInlineEdit({ ...inlineEdit, value: e.target.value })}>
                  {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            ) : (
                <input type={type} value={inlineEdit.value || ''} onChange={(e) => setInlineEdit({ ...inlineEdit, value: e.target.value })} />
            )}
            <button type="button" className="pm-inline-btn pm-inline-btn-save" onClick={onSave}>✔</button>
            <button type="button" className="pm-inline-btn pm-inline-btn-cancel" onClick={onCancel}>✖</button>
          </div>
      )}
    </td>
);

const ProductsAdmin = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState('active');
  const [searchText, setSearchText] = useState('');
  const [parentFilterId, setParentFilterId] = useState('');
  const [childFilterId, setChildFilterId] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sort, setSort] = useState({ col: 'created_at', dir: 'desc' });
  const [inlineEdit, setInlineEdit] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterCondition, setFilterCondition] = useState([]);
  const [filterManufacturer, setFilterManufacturer] = useState([]);
  const [filterModel, setFilterModel] = useState([]);
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [filterFeatured, setFilterFeatured] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    // eslint-disable-next-line
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/products/all`);
      setProducts(res.data || []);
    } catch (err) {
      setError('Failed to fetch products');
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/categories`);
      setCategories(res.data || []);
    } catch (err) {
      console.error('Failed to fetch categories', err);
    }
  };

  const buildCategoryPath = (categoryId) => {
    if (!categoryId || !categories.length) return '';
    const chain = [];
    let currentId = categoryId;
    while (currentId) {
      const cat = categories.find((c) => c.id === currentId);
      if (!cat) break;
      chain.unshift(cat.name);
      currentId = cat.parent_id;
    }
    return chain.join(' > ');
  };

  const getDescendantCategoryIds = (rootId) => {
    const ids = new Set();
    if (!rootId) return ids;
    const queue = [rootId];
    ids.add(rootId);
    while (queue.length) {
      const id = queue.shift();
      categories.filter((c) => c.parent_id === id).forEach((child) => {
        if (!ids.has(child.id)) { ids.add(child.id); queue.push(child.id); }
      });
    }
    return ids;
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try { return new Date(value).toLocaleDateString('en-GB'); } catch { return value; }
  };

  const daysUntilPurge = (deletedAt) => {
    if (!deletedAt) return null;
    const diff = 30 - Math.floor((Date.now() - new Date(deletedAt)) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const buildProductPayload = (product, overrides = {}) => {
    const merged = { ...product, ...overrides };
    return {
      name: merged.name || '',
      description: merged.description || '',
      price: merged.price,
      price_on_application: merged.price_on_application,
      categoryId: merged.category_id ?? merged.categoryId,
      manufacturer: merged.manufacturer,
      model: merged.model,
      condition: merged.condition,
      year: merged.year,
      quantity: merged.quantity,
      mpn: merged.mpn,
      files: merged.files || [],
      display_image: merged.display_image || null,
      enabled: merged.enabled,
      sold: merged.sold || false,
    };
  };

  const topCategories = categories.filter((c) => c.parent_id == null);
  const childCategories = parentFilterId
      ? categories.filter((c) => c.parent_id === parseInt(parentFilterId, 10))
      : [];
  const selectedCategoryId = childFilterId || parentFilterId;

  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (tab === 'active')   list = list.filter((p) => !p.deleted_at && p.enabled && !p.sold);
    if (tab === 'inactive') list = list.filter((p) => !p.deleted_at && !p.enabled && !p.sold);
    if (tab === 'sold')     list = list.filter((p) => !p.deleted_at && p.sold);
    if (tab === 'deleted')  list = list.filter((p) => !!p.deleted_at);

    if (selectedCategoryId) {
      const idsSet = getDescendantCategoryIds(parseInt(selectedCategoryId, 10));
      list = list.filter((p) => idsSet.has(p.category_id));
    }

    if (searchText.trim()) {
      const ft = searchText.toLowerCase();
      list = list.filter((p) =>
          (p.name || '').toLowerCase().includes(ft) ||
          (p.description || '').toLowerCase().includes(ft) ||
          (p.model || '').toLowerCase().includes(ft)
      );
    }

    if (filterFeatured) {
      list = list.filter((p) => p.featured === true);
    }

    if (filterCondition.length > 0) {
      list = list.filter((p) => filterCondition.includes(p.condition || ''));
    }
    if (filterManufacturer.length > 0) {
      list = list.filter((p) => filterManufacturer.includes(p.manufacturer || ''));
    }
    if (filterModel.length > 0) {
      list = list.filter((p) => filterModel.includes(p.model || ''));
    }
    if (filterPriceMin !== '') {
      list = list.filter((p) => p.price != null && Number(p.price) >= Number(filterPriceMin));
    }
    if (filterPriceMax !== '') {
      list = list.filter((p) => p.price != null && Number(p.price) <= Number(filterPriceMax));
    }

    list.sort((a, b) => {
      let aVal, bVal;
      if (sort.col === 'created_at') {
        aVal = a.created_at ? new Date(a.created_at) : new Date(0);
        bVal = b.created_at ? new Date(b.created_at) : new Date(0);
      } else if (sort.col === 'deleted_at') {
        aVal = a.deleted_at ? new Date(a.deleted_at) : new Date(0);
        bVal = b.deleted_at ? new Date(b.deleted_at) : new Date(0);
      } else if (sort.col === 'category') {
        aVal = buildCategoryPath(a.category_id).toLowerCase();
        bVal = buildCategoryPath(b.category_id).toLowerCase();
      } else if (sort.col === 'name') {
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
      } else if (sort.col === 'model') {
        aVal = (a.model || '').toLowerCase();
        bVal = (b.model || '').toLowerCase();
      } else if (sort.col === 'price') {
        aVal = a.price != null ? Number(a.price) : -1;
        bVal = b.price != null ? Number(b.price) : -1;
      } else {
        aVal = a[sort.col] ?? '';
        bVal = b[sort.col] ?? '';
      }
      if (aVal < bVal) return sort.dir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
    // eslint-disable-next-line
  }, [products, tab, parentFilterId, childFilterId, searchText, sort, categories, filterCondition, filterManufacturer, filterModel, filterPriceMin, filterPriceMax, filterFeatured]);

  const handleSort = (col) => {
    setSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  };

  const sortIcon = (col) => {
    if (sort.col !== col) return <span className="pm-sort-icon">↕</span>;
    return <span className="pm-sort-icon pm-sort-active">{sort.dir === 'asc' ? '↑' : '↓'}</span>;
  };

  const totalPages = Math.ceil(filteredProducts.length / perPage);
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * perPage, currentPage * perPage);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    const start = Math.max(2, currentPage - 2);
    const end   = Math.min(totalPages - 1, currentPage + 2);
    pages.push(1);
    if (start > 2) pages.push('…');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push('…');
    pages.push(totalPages);
    return pages;
  }, [totalPages, currentPage]);

  const visibleIds = paginatedProducts.map((p) => p.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const tabCounts = useMemo(() => ({
    active:   products.filter((p) => !p.deleted_at && p.enabled && !p.sold).length,
    inactive: products.filter((p) => !p.deleted_at && !p.enabled && !p.sold).length,
    sold:     products.filter((p) => !p.deleted_at && p.sold).length,
    deleted:  products.filter((p) => !!p.deleted_at).length,
  }), [products]);

  // ---- inline edit ----

  const startInlineEdit = (product, field) => {
    let initial = '';
    if (field === 'name') initial = product.name || '';
    if (field === 'manufacturer') initial = product.manufacturer || '';
    if (field === 'model') initial = product.model || '';
    if (field === 'condition') initial = product.condition || '';
    if (field === 'year') initial = product.year || '';
    if (field === 'category') initial = product.category_id || '';
    if (field === 'price') initial = product.price !== null && product.price !== undefined ? product.price : '';
    setInlineEdit({ productId: product.id, field, value: initial });
  };

  const cancelInlineEdit = () => setInlineEdit(null);

  const handleInlineSave = async () => {
    if (!inlineEdit) return;
    const product = products.find((p) => p.id === inlineEdit.productId);
    if (!product) { setInlineEdit(null); return; }

    let overrides = {};
    switch (inlineEdit.field) {
      case 'name':         overrides = { name: inlineEdit.value }; break;
      case 'manufacturer': overrides = { manufacturer: inlineEdit.value || null }; break;
      case 'model':        overrides = { model: inlineEdit.value || null }; break;
      case 'condition':    overrides = { condition: inlineEdit.value || null }; break;
      case 'year':         overrides = { year: inlineEdit.value || null }; break;
      case 'category':
        overrides = {
          category_id: inlineEdit.value ? parseInt(inlineEdit.value, 10) : null,
          categoryId:  inlineEdit.value ? parseInt(inlineEdit.value, 10) : null,
        };
        break;
      case 'price':
        overrides = { price: inlineEdit.value === '' ? null : inlineEdit.value };
        break;
      default: break;
    }

    try {
      await axios.put(`${API_BASE_URL}/api/products/${product.id}`, buildProductPayload(product, overrides));
      await fetchProducts();
      setInlineEdit(null);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update product');
    }
  };

  // ---- selection ----

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleToggleSelectAll = () => {
    const allSelected = visibleIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  // ---- soft delete ----

  const handleSoftDelete = async (ids) => {
    if (ids.length === 0) { alert('No products selected.'); return; }
    try {
      await Promise.all(ids.map((id) => axios.patch(`${API_BASE_URL}/api/products/${id}/soft-delete`)));
      await fetchProducts();
      setSelectedIds([]);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete products');
    }
  };

  // ---- restore ----

  const handleRestore = async (ids) => {
    if (ids.length === 0) { alert('No products selected.'); return; }
    try {
      await Promise.all(ids.map((id) => axios.patch(`${API_BASE_URL}/api/products/${id}/restore`)));
      await fetchProducts();
      setSelectedIds([]);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to restore products');
    }
  };

  // ---- permanent delete ----

  const openPermanentDelete = (ids) => {
    if (ids.length === 0) { alert('No products selected.'); return; }
    setDeleteConfirm({ ids, confirmText: '' });
  };

  const handlePermanentDelete = async () => {
    if (!deleteConfirm || deleteConfirm.confirmText !== 'DELETE') return;
    try {
      await Promise.all(deleteConfirm.ids.map((id) => axios.delete(`${API_BASE_URL}/api/products/${id}/permanent`)));
      await fetchProducts();
      setSelectedIds([]);
      setDeleteConfirm(null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to permanently delete products');
      setDeleteConfirm(null);
    }
  };

  // ---- enable/sold toggles ----

  const updateMany = async (ids, overrides) => {
    if (ids.length === 0) { alert('No products selected.'); return; }
    try {
      await Promise.all(ids.map((id) => {
        const product = products.find((p) => p.id === id);
        if (!product) return null;
        return axios.put(`${API_BASE_URL}/api/products/${id}`, buildProductPayload(product, overrides));
      }));
      await fetchProducts();
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to update products');
    }
  };

  const handleToggleEnabledSingle = async (product) => {
    try {
      await axios.put(`${API_BASE_URL}/api/products/${product.id}`, buildProductPayload(product, { enabled: !product.enabled }));
      await fetchProducts();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to toggle enabled');
    }
  };

  const handleToggleSoldSingle = async (product) => {
    try {
      await axios.put(`${API_BASE_URL}/api/products/${product.id}`, buildProductPayload(product, { sold: !product.sold, enabled: product.sold ? true : false }));
      await fetchProducts();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to toggle sold');
    }
  };

  const handleToggleFeatured = async (product) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/products/${product.id}/featured`);
      await fetchProducts();
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to toggle featured');
    }
  };

  // ---- duplicate (copy without photos) ----

  const [duplicating, setDuplicating] = useState(null);

  const handleDuplicate = async (product) => {
    setDuplicating(product.id);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/products/${product.id}/duplicate`);
      await fetchProducts();
      setError('');
      // Jump straight into editing the new copy so photos/condition can be added
      navigate(`/admin/products/edit/${res.data.id}`);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to duplicate product');
    } finally {
      setDuplicating(null);
    }
  };

  // shared props passed down to InlineEditor
  const editorProps = { inlineEdit, setInlineEdit, onSave: handleInlineSave, onCancel: cancelInlineEdit, onStart: startInlineEdit, buildCategoryPath };

  // ---- side panel filter helpers ----
  const tabProducts = useMemo(() => {
    let list = [...products];
    if (tab === 'active')   list = list.filter((p) => !p.deleted_at && p.enabled && !p.sold);
    if (tab === 'inactive') list = list.filter((p) => !p.deleted_at && !p.enabled && !p.sold);
    if (tab === 'sold')     list = list.filter((p) => !p.deleted_at && p.sold);
    if (tab === 'deleted')  list = list.filter((p) => !!p.deleted_at);
    return list;
  }, [products, tab]);

  const availableConditions    = useMemo(() => [...new Set(tabProducts.map((p) => p.condition).filter(Boolean))].sort(), [tabProducts]);
  const availableManufacturers = useMemo(() => [...new Set(tabProducts.map((p) => p.manufacturer).filter(Boolean))].sort(), [tabProducts]);
  const availableModels        = useMemo(() => [...new Set(tabProducts.map((p) => p.model).filter(Boolean))].sort(), [tabProducts]);

  const activePanelFilterCount =
      filterCondition.length + filterManufacturer.length + filterModel.length +
      (filterPriceMin !== '' ? 1 : 0) + (filterPriceMax !== '' ? 1 : 0) +
      (childFilterId ? 1 : 0) +
      (filterFeatured ? 1 : 0);

  const resetPanelFilters = () => {
    setFilterCondition([]);
    setFilterManufacturer([]);
    setFilterModel([]);
    setFilterPriceMin('');
    setFilterPriceMax('');
    setChildFilterId('');
    setFilterFeatured(false);
  };

  const toggleArr = (arr, setArr, val) =>
      arr.includes(val) ? setArr(arr.filter((x) => x !== val)) : setArr([...arr, val]);

  return (
      <div className="products-admin">
        <h1>Product Manager</h1>

        {/* ===== TABS ===== */}
        <div className="pm-tabs">
          {TABS.map((t) => (
              <button key={t}
                      className={`pm-tab ${tab === t ? 'pm-tab-active' : ''} ${t === 'deleted' ? 'pm-tab-deleted' : ''}`}
                      onClick={() => { setTab(t); setSelectedIds([]); setCurrentPage(1); }}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
                <span className="pm-tab-count">{tabCounts[t]}</span>
              </button>
          ))}
        </div>

        {/* ===== TOOLBAR ===== */}
        <div className="products-toolbar">
          <div className="products-toolbar-left">
            <div className="products-toolbar-filter-group">
              <div className="products-toolbar-filter-item products-toolbar-cat-item">
                <i className="fas fa-filter products-toolbar-filter-icon" />
                <select className="products-toolbar-select" value={parentFilterId}
                        onChange={(e) => { setParentFilterId(e.target.value); setChildFilterId(''); setCurrentPage(1); }}>
                  <option value="">All categories</option>
                  {topCategories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>

              <div className="products-toolbar-filter-divider" />
              <div className="products-toolbar-filter-item">
                <i className="fas fa-search products-toolbar-filter-icon" />
                <input type="text" className="products-toolbar-search" placeholder="Search name, description, model..."
                       value={searchText} onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }} />
              </div>

              <div className="products-toolbar-filter-divider" />
              <button
                  className={`products-toolbar-filter-btn ${activePanelFilterCount > 0 ? 'products-toolbar-filter-btn-active' : ''}`}
                  onClick={() => setFilterPanelOpen(true)}
                  title="Advanced filters"
              >
                <i className="fas fa-sliders-h" />
                {activePanelFilterCount > 0 && <span className="products-toolbar-filter-badge">{activePanelFilterCount}</span>}
              </button>
            </div>

            {/* Featured quick-filter pill */}
            <button
                className={`pm-featured-filter-pill${filterFeatured ? ' pm-featured-filter-pill-on' : ''}`}
                onClick={() => { setFilterFeatured((v) => !v); setCurrentPage(1); }}
                title="Show featured only"
            >
              ★ Featured
            </button>
          </div>

          <div className="products-toolbar-right">
            {tab !== 'deleted' && (
                <button className="products-toolbar-btn products-toolbar-insert" onClick={() => navigate('/admin/products/addproduct')}>
                  <i className="fas fa-plus" /> Add
                </button>
            )}
            {tab !== 'sold' && tab !== 'deleted' && (
                <>
                  <div className="products-toolbar-btn-divider" />
                  <button className="products-toolbar-btn products-toolbar-enable" onClick={() => updateMany(selectedIds, { enabled: true })}>
                    <i className="fas fa-eye" /> Enable
                  </button>
                  <button className="products-toolbar-btn products-toolbar-disable" onClick={() => updateMany(selectedIds, { enabled: false })}>
                    <i className="fas fa-eye-slash" /> Disable
                  </button>
                  <button className="products-toolbar-btn products-toolbar-sold" onClick={() => updateMany(selectedIds, { sold: true, enabled: false })}>
                    <i className="fas fa-tag" /> Sold
                  </button>
                </>
            )}
            {tab === 'sold' && (
                <button className="products-toolbar-btn products-toolbar-enable" onClick={() => updateMany(selectedIds, { sold: false, enabled: true })}>
                  <i className="fas fa-redo" /> Relist
                </button>
            )}
            {tab === 'deleted' && (
                <button className="products-toolbar-btn products-toolbar-enable" onClick={() => handleRestore(selectedIds)}>
                  <i className="fas fa-undo" /> Restore
                </button>
            )}
            <div className="products-toolbar-btn-divider" />
            {tab === 'deleted' ? (
                <button className="products-toolbar-btn products-toolbar-delete" onClick={() => openPermanentDelete(selectedIds)}>
                  <i className="fas fa-trash" /> Delete Forever
                </button>
            ) : (
                <button className="products-toolbar-btn products-toolbar-delete" onClick={() => handleSoftDelete(selectedIds)}>
                  <i className="fas fa-trash" /> Delete
                </button>
            )}
          </div>
        </div>

        {tab === 'deleted' && (
            <p className="pm-deleted-notice">Items in the bin are permanently deleted after 30 days.</p>
        )}

        {error && <p style={{ color: 'red', marginBottom: 8 }}>{error}</p>}

        {/* ===== TABLE ===== */}
        <div className="products-table-container">
          <table>
            <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input type="checkbox" checked={allVisibleSelected} onChange={handleToggleSelectAll} />
              </th>
              <th>ID</th>
              <th>Img</th>
              <th className="pm-sortable" onClick={() => handleSort('name')}>Name {sortIcon('name')}</th>
              <th className="pm-sortable" onClick={() => handleSort('category')}>Category {sortIcon('category')}</th>
              <th className="pm-sortable" onClick={() => handleSort('model')}>Model {sortIcon('model')}</th>
              <th className="pm-sortable" onClick={() => handleSort('manufacturer')}>Manufacturer {sortIcon('manufacturer')}</th>
              <th className="pm-sortable" onClick={() => handleSort('condition')}>Condition {sortIcon('condition')}</th>
              <th className="pm-sortable" onClick={() => handleSort('year')}>Year {sortIcon('year')}</th>
              <th className="pm-sortable" onClick={() => handleSort('price')}>Price {sortIcon('price')}</th>
              <th className="pm-sortable" onClick={() => handleSort('created_at')}>Date Added {sortIcon('created_at')}</th>
              {tab === 'deleted' && <th className="pm-sortable" onClick={() => handleSort('deleted_at')}>Deleted {sortIcon('deleted_at')}</th>}
              {tab !== 'deleted' && <th>Status</th>}
              <th style={{ width: 130 }}>Actions</th>
            </tr>
            </thead>
            <tbody>
            {paginatedProducts.map((product) => {
              const isSelected = selectedIds.includes(product.id);
              const editing = (field) => inlineEdit && inlineEdit.productId === product.id && inlineEdit.field === field;
              const purge = daysUntilPurge(product.deleted_at);

              return (
                  <tr key={product.id} className={product.sold ? 'pm-row-sold' : product.deleted_at ? 'pm-row-deleted' : ''}>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={isSelected} onChange={() => handleCheckboxChange(product.id)} />
                    </td>
                    <td>{product.id}</td>
                    <td className="products-table-image-cell" style={{ cursor: 'pointer' }} onClick={() => navigate(`/product/${product.id}`)}>
                      <img
                          src={product.display_image || '/images/defaultimg.png'}
                          alt={product.name}
                          className="products-table-image"
                      />
                    </td>

                    <InlineEditor product={product} field="name" isEditing={editing('name')} {...editorProps} />
                    <InlineEditor product={product} field="category" isEditing={editing('category')}
                                  options={[{ value: '', label: '(blank)' }, ...categories.map((c) => ({ value: c.id, label: buildCategoryPath(c.id) }))]}
                                  {...editorProps} />
                    <InlineEditor product={product} field="model" isEditing={editing('model')} {...editorProps} />
                    <InlineEditor product={product} field="manufacturer" isEditing={editing('manufacturer')} {...editorProps} />
                    <InlineEditor product={product} field="condition" isEditing={editing('condition')}
                                  options={[{ value: '', label: '(blank)' }, { value: 'New', label: 'New' }, { value: 'Used', label: 'Used' }]}
                                  {...editorProps} />
                    <InlineEditor product={product} field="year" isEditing={editing('year')} type="number" {...editorProps} />
                    <InlineEditor product={product} field="price" isEditing={editing('price')} type="number" {...editorProps} />

                    <td>{formatDate(product.created_at)}</td>

                    {tab === 'deleted' && (
                        <td>
                          <div>{formatDate(product.deleted_at)}</div>
                          {purge !== null && (
                              <div className={`pm-purge-days ${purge <= 5 ? 'pm-purge-urgent' : ''}`}>{purge}d left</div>
                          )}
                        </td>
                    )}

                    {tab !== 'deleted' && (
                        <td>
                          <div className="pm-badges">
                            {product.sold ? (
                                <span className="badge badge-sold" onClick={() => handleToggleSoldSingle(product)} style={{ cursor: 'pointer' }} title="Click to relist">Sold</span>
                            ) : (
                                <>
                            <span className={product.enabled ? 'badge badge-enabled' : 'badge badge-disabled'}
                                  onClick={() => handleToggleEnabledSingle(product)} style={{ cursor: 'pointer' }}>
                              {product.enabled ? 'Active' : 'Inactive'}
                            </span>
                                  <span className="badge badge-marksold" onClick={() => handleToggleSoldSingle(product)} style={{ cursor: 'pointer' }} title="Mark as sold">Sold?</span>
                                </>
                            )}
                            <span
                                className={`pm-featured-btn${product.featured ? ' pm-featured-btn-on' : ''}`}
                                onClick={() => handleToggleFeatured(product)}
                                title={product.featured ? 'Remove from featured' : 'Set as featured (max 3)'}
                            >★</span>
                          </div>
                        </td>
                    )}

                    <td>
                      {tab === 'deleted' ? (
                          <>
                            <button className="pm-row-btn pm-row-btn-edit" onClick={() => handleRestore([product.id])}>Restore</button>
                            <button className="pm-row-btn pm-row-btn-delete" onClick={() => openPermanentDelete([product.id])}>Delete</button>
                          </>
                      ) : (
                          <>
                            <button className="pm-row-btn pm-row-btn-edit" onClick={() => navigate(`/admin/products/edit/${product.id}`)}>Edit</button>
                            <button className="pm-row-btn pm-row-btn-copy" title="Duplicate this listing without photos — gives it a new stock number so you can quickly list another unit of the same item"
                                    disabled={duplicating === product.id}
                                    onClick={() => handleDuplicate(product)}>
                              {duplicating === product.id ? 'Copying…' : 'Copy'}
                            </button>
                            <button className="pm-row-btn pm-row-btn-delete" onClick={() => handleSoftDelete([product.id])}>Delete</button>
                          </>
                      )}
                    </td>
                  </tr>
              );
            })}

            {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={14} style={{ textAlign: 'center', padding: 16, color: '#888' }}>
                    No {tab} products found.
                  </td>
                </tr>
            )}
            </tbody>
          </table>
        </div>

        {/* ===== PAGINATION ===== */}
        {filteredProducts.length > 0 && (
            <div className="pm-pagination">
              <div className="pm-pagination-info">
                Showing {Math.min((currentPage - 1) * perPage + 1, filteredProducts.length)}–{Math.min(currentPage * perPage, filteredProducts.length)} of {filteredProducts.length}
              </div>
              <div className="pm-pagination-controls">
                <button className="pm-page-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</button>
                {pageNumbers.map((n, i) =>
                    n === '…'
                        ? <span key={`ellipsis-${i}`} className="pm-page-ellipsis">…</span>
                        : <button key={n} className={`pm-page-btn ${currentPage === n ? 'pm-page-btn-active' : ''}`} onClick={() => setCurrentPage(n)}>{n}</button>
                )}
                <button className="pm-page-btn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>›</button>
              </div>
              <div className="pm-per-page">
                <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                  <option value={10}>10 / page</option>
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
            </div>
        )}
        {deleteConfirm && (
            <div className="pm-modal-overlay">
              <div className="pm-modal">
                <h3>Permanently Delete {deleteConfirm.ids.length} Product{deleteConfirm.ids.length > 1 ? 's' : ''}?</h3>
                <p>This will permanently remove the product and all its images from S3. <strong>This cannot be undone.</strong></p>
                <p>Type <strong>DELETE</strong> to confirm:</p>
                <input type="text" className="pm-modal-input" placeholder="Type DELETE to confirm"
                       value={deleteConfirm.confirmText}
                       onChange={(e) => setDeleteConfirm({ ...deleteConfirm, confirmText: e.target.value })}
                       autoFocus />
                <div className="pm-modal-actions">
                  <button className="pm-modal-btn pm-modal-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                  <button className="pm-modal-btn pm-modal-confirm"
                          disabled={deleteConfirm.confirmText !== 'DELETE'}
                          onClick={handlePermanentDelete}>
                    Delete Forever
                  </button>
                </div>
              </div>
            </div>
        )}

        {/* ===== FILTER PANEL ===== */}
        {filterPanelOpen && (
            <div className="pm-fp-overlay" onClick={() => setFilterPanelOpen(false)} />
        )}
        <div className={`pm-fp ${filterPanelOpen ? 'pm-fp-open' : ''}`}>
          <div className="pm-fp-header">
            <span className="pm-fp-title"><i className="fas fa-sliders-h" /> Filters</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {activePanelFilterCount > 0 && (
                  <button className="pm-fp-clear" onClick={resetPanelFilters}>Clear all</button>
              )}
              <button className="pm-fp-close" onClick={() => setFilterPanelOpen(false)}>✕</button>
            </div>
          </div>

          {/* Featured */}
          <div className="pm-fp-section">
            <div className="pm-fp-section-title">Featured</div>
            <label className="pm-fp-check-label">
              <input type="checkbox" checked={filterFeatured}
                     onChange={() => { setFilterFeatured((v) => !v); setCurrentPage(1); }} />
              Show featured only
            </label>
          </div>

          {/* Sub-category — only when parent selected */}
          {parentFilterId && childCategories.length > 0 && (
              <div className="pm-fp-section">
                <div className="pm-fp-section-title">Sub-category</div>
                {childCategories.map((cat) => (
                    <label key={cat.id} className="pm-fp-check-label">
                      <input type="radio" name="subcat"
                             checked={childFilterId === String(cat.id)}
                             onChange={() => { setChildFilterId(String(cat.id)); setCurrentPage(1); }} />
                      {cat.name}
                    </label>
                ))}
                {childFilterId && (
                    <button className="pm-fp-deselect" onClick={() => { setChildFilterId(''); setCurrentPage(1); }}>
                      Show all subcategories
                    </button>
                )}
              </div>
          )}

          {/* Price range */}
          <div className="pm-fp-section">
            <div className="pm-fp-section-title">Price (£)</div>
            <div className="pm-fp-price-row">
              <input type="number" className="pm-fp-price-input" placeholder="Min"
                     value={filterPriceMin} onChange={(e) => { setFilterPriceMin(e.target.value); setCurrentPage(1); }} />
              <span className="pm-fp-price-sep">–</span>
              <input type="number" className="pm-fp-price-input" placeholder="Max"
                     value={filterPriceMax} onChange={(e) => { setFilterPriceMax(e.target.value); setCurrentPage(1); }} />
            </div>
          </div>

          {/* Condition */}
          {availableConditions.length > 0 && (
              <div className="pm-fp-section">
                <div className="pm-fp-section-title">Condition</div>
                {availableConditions.map((c) => (
                    <label key={c} className="pm-fp-check-label">
                      <input type="checkbox" checked={filterCondition.includes(c)}
                             onChange={() => { toggleArr(filterCondition, setFilterCondition, c); setCurrentPage(1); }} />
                      {c}
                    </label>
                ))}
              </div>
          )}

          {/* Manufacturer */}
          {availableManufacturers.length > 0 && (
              <div className="pm-fp-section">
                <div className="pm-fp-section-title">Manufacturer</div>
                {availableManufacturers.map((m) => (
                    <label key={m} className="pm-fp-check-label">
                      <input type="checkbox" checked={filterManufacturer.includes(m)}
                             onChange={() => { toggleArr(filterManufacturer, setFilterManufacturer, m); setCurrentPage(1); }} />
                      {m}
                    </label>
                ))}
              </div>
          )}

          {/* Model */}
          {availableModels.length > 0 && (
              <div className="pm-fp-section">
                <div className="pm-fp-section-title">Model</div>
                {availableModels.map((m) => (
                    <label key={m} className="pm-fp-check-label">
                      <input type="checkbox" checked={filterModel.includes(m)}
                             onChange={() => { toggleArr(filterModel, setFilterModel, m); setCurrentPage(1); }} />
                      {m}
                    </label>
                ))}
              </div>
          )}
        </div>
      </div>
  );
};

export default ProductsAdmin;